// useSessionUser.ts
/**
 * Liste d’étudiants (session / filtres).
 * - Mode "client" : fetchAllEtudiants agrège toutes les pages Django ; fetchEtudiants = une page.
 * - lastFetchTruncated est mis à true si la pagination n'a pas pu être complétée.
 */
import {computed, reactive, ref} from "vue";
import {useApi} from "@/composables/useApi";
import {toast} from "vue3-toastify";
import {
    ETUDIANTS_BIS_PAGE_SIZE,
    fetchAllEtudiantsBisPages,
    type EtudiantsBisPagedResponse,
    type FetchAllEtudiantsBisResult,
} from "@/utils/etudiantsBisPagination";

interface Filters {
    search: string;
}

interface Pagination {
    total: number;
    current_page: number;
    per_page: number;
    last_page: number;
    nb_licence?: number;
    nb_master?: number;
}

interface State {
    etudiants: any[];
    loading: boolean;
    errors: Record<string, string[]>;
    filters: Filters;
    pagination: Pagination;
}

export function useSessionUser() {
    const {apiDjangoAuth} = useApi();
    const loadingSubmit = ref(false);

    // Suivi des liens de pagination (comme useUtilisateur)
    const nextUrl = ref<string | null>(null);
    const prevUrl = ref<string | null>(null);
    /** True si la dernière réponse était plafonnée (plus de résultats en backend qu’affichés). */
    const lastFetchTruncated = ref(false);

    /**
     * Fonction pour extraire le chemin relatif de l'URL
     * (enlever le base URL et garder seulement le path + query params)
     * En enlevant aussi le préfixe /api qui sera ajouté par baseURL
     */
    function extractRelativePath(url: string | null): string | null {
        if (!url) return null;
        try {
            const urlObj = new URL(url);
            let path = urlObj.pathname + urlObj.search;
            // Enlever le préfixe /api/ si présent, car baseURL l'ajoutera automatiquement
            if (path.startsWith('/api/')) {
                path = path.substring(4); // Enlève '/api'
            }
            return path;
        } catch (error) {
            // Si l'URL n'est pas valide ou est déjà relative, la retourner telle quelle
            return url;
        }
    }

    const state = reactive<State>({
        etudiants: [],
        loading: false,
        errors: {},
        filters: {
            search: "",
        },
        pagination: {
            total: 0,
            current_page: 1,
            per_page: 100,
            last_page: 1,
            nb_licence: 0,
            nb_master: 0,
        },
    });

    /**
     * Ancien plafond "tout en un" (héritage UI).
     * Ne doit plus servir de hard-cap API : l'agrégation multi-pages n'a pas de limite.
     */
    const CLIENT_SIDE_LIMIT = 9999;

    /** Limite effective pour un appel mono-page : -1 (Tous) → CLIENT_SIDE_LIMIT, sinon per_page. */
    function effectiveLimit(perPage: number) {
        return perPage === -1 || perPage < 1 ? CLIENT_SIDE_LIMIT : perPage;
    }

    /**
     * Construit les paramètres API par défaut et applique les overrides.
     * - Django etudiants_bis utilise surtout `limit` comme taille de page
     * - en agrégation : limit = per_page = page_size (ex. 100), jamais 9999
     */
    function buildParams(overrides: Record<string, any> = {}) {
        const hasExplicitPageSize =
            overrides.per_page != null || overrides.page_size != null;

        const base = {
            q: state.filters.search || "",
            page: state.pagination.current_page,

            // filtres (pass-through si fournis en overrides)
            anneescolaire: undefined as any,
            debut_inscription: undefined as any,
            fin_inscription: undefined as any,
            filiere: undefined as any,
            nationalite: undefined as any,
            specialite: undefined as any,
            ...overrides,
        } as Record<string, any>;

        if (hasExplicitPageSize) {
            // Taille de page API : aligner limit/per_page/page_size (Django lit surtout `limit`)
            const pageSize = Number(base.per_page ?? base.page_size) || ETUDIANTS_BIS_PAGE_SIZE;
            base.limit = pageSize;
            base.per_page = pageSize;
            base.page_size = pageSize;
        } else {
            base.limit = effectiveLimit(base.limit ?? state.pagination.per_page);
        }

        // Nettoyage: on retire undefined / "" / null mais on garde 0 / false
        return Object.fromEntries(
            Object.entries(base).filter(
                ([, v]) => v !== undefined && v !== "" && v !== null
            )
        );
    }

    /**
     * Appel HTTP etudiants_bis sans toucher au store (agrégation multi-pages).
     */
    async function requestEtudiantsBis(
        arg: string | Record<string, any> = {}
    ): Promise<EtudiantsBisPagedResponse> {
        if (typeof arg === "string" || (typeof arg === "object" && (arg as any).url)) {
            const url = typeof arg === "string" ? arg : (arg as any).url;
            return (await apiDjangoAuth(url)) as EtudiantsBisPagedResponse;
        }

        const params = buildParams(arg as Record<string, any>);
        return (await apiDjangoAuth("/sessions/etudiants_bis/", {
            params,
        })) as EtudiantsBisPagedResponse;
    }

    function applyEtudiantsResponseToState(response: EtudiantsBisPagedResponse) {
        state.etudiants = Array.isArray(response?.results) ? response.results : [];
        const apiCount = Number(response?.count || 0) || 0;
        state.pagination.total = apiCount;
        state.pagination.nb_licence = Number(response?.nb_licence || 0) || 0;
        state.pagination.nb_master = Number(response?.nb_master || 0) || 0;

        nextUrl.value = extractRelativePath(response?.next ?? null);
        prevUrl.value = extractRelativePath(response?.previous ?? null);
        const isCapped = !!(nextUrl.value && state.etudiants.length < apiCount);
        lastFetchTruncated.value = isCapped;
        if (isCapped) {
            state.pagination.total = state.etudiants.length;
            state.pagination.current_page = 1;
        }

        state.pagination.last_page =
            state.pagination.per_page === -1
                ? 1
                : Math.max(
                    1,
                    Math.ceil(
                        state.pagination.total / Math.max(1, state.pagination.per_page)
                    )
                );

        if (!isCapped) {
            if (!nextUrl.value && !prevUrl.value) {
                state.pagination.current_page = 1;
            } else if (nextUrl.value) {
                try {
                    const match = nextUrl.value.match(/[?&]page=(\d+)/);
                    if (match) state.pagination.current_page = Number(match[1]) - 1;
                } catch {}
            } else if (prevUrl.value) {
                try {
                    const match = prevUrl.value.match(/[?&]page=(\d+)/);
                    if (match) state.pagination.current_page = Number(match[1]) + 1;
                } catch {}
            }
        }
    }

    /** Une page etudiants_bis pour agrégation — ne met pas à jour le store. */
    async function fetchEtudiantsBisPageSilent(
        params: Record<string, unknown> = {},
        _showLoading = false
    ): Promise<EtudiantsBisPagedResponse> {
        // Un seul buildParams (évite de réinjecter limit après nettoyage)
        return requestEtudiantsBis(params as Record<string, any>);
    }

    /**
     * Récupère la liste (accepte:
     *  - une URL next/prev (string),
     *  - un objet params (ex: { page, page_size, q, session, ... }),
     *  - ou { nav: "next"|"prev" } pour compatibilité temporaire avec l’UI actuelle).
     */
    async function fetchEtudiants(arg: string | Record<string, any> = {}, useloading: boolean = true) {
        try {
            if (useloading)
                state.loading = true;
            let response: EtudiantsBisPagedResponse;

            // --- Compat nav: { nav: "next" | "prev" }
            if (typeof arg !== "string" && (arg as any).nav) {
                const dir = (arg as any).nav;
                const url =
                    dir === "next"
                        ? nextUrl.value
                        : dir === "prev"
                        ? prevUrl.value
                        : null;

                if (url) {
                    response = await requestEtudiantsBis(url);
                } else {
                    response = await requestEtudiantsBis(arg as Record<string, any>);
                }
            }

            // --- URL directe (string) ou { url }
            else if (
                typeof arg === "string" ||
                (typeof arg === "object" && (arg as any).url)
            ) {
                response = await requestEtudiantsBis(arg);
            }

            // --- Appel paramétré
            else {
                response = await requestEtudiantsBis(arg as Record<string, any>);
            }

            applyEtudiantsResponseToState(response);
            return response;

        } catch (err: any) {
            //console.error("Erreur lors de fetchEtudiants :", err);
            state.errors.general = [
                (err?.message as string) ||
                "Erreur lors de la récupération des étudiants",
            ];
            state.etudiants = [];
            state.pagination.total = 0;
            state.pagination.nb_licence = 0;
            state.pagination.nb_master = 0;
            state.pagination.current_page = 1;
            state.pagination.last_page = 1;
            lastFetchTruncated.value = false;
        } finally {
            if (useloading)
                state.loading = false;
        }
    }

    // Navigation "à la useUtilisateur"
    async function loadNext() {
        if (nextUrl.value) {
            await fetchEtudiants(nextUrl.value);
        }
    }

    async function loadPrevious() {
        if (prevUrl.value) {
            await fetchEtudiants(prevUrl.value);
        }
    }

    // Aller directement à une page donnée (utile pour v-data-table-server)
    async function goToPage(targetPage: number) {
        if (targetPage < 1) return;
        await fetchEtudiants({
            page: targetPage,
            limit: effectiveLimit(state.pagination.per_page),
        });
    }

    // API utilitaires déjà utilisées par la page
    async function filterInformation(params: any, useloading: boolean = true) {
        return await fetchEtudiants(params, useloading);
    }

    /**
     * Charge toutes les pages etudiants_bis pour les filtres donnés (session/spécialité, etc.).
     * Met à jour le store avec la liste complète ; lastFetchTruncated si pagination incomplète.
     */
    async function fetchAllEtudiants(
        params: Record<string, any> = {},
        useloading: boolean = true
    ): Promise<FetchAllEtudiantsBisResult & EtudiantsBisPagedResponse> {
        try {
            if (useloading) state.loading = true;

            // Paginer par tranches de 100 jusqu'à épuisement de `next` (maxPages=0 = illimité).
            // On retire un éventuel limit=9999 des callers ; l'agrégateur renvoie limit=100/page.
            const perPage = ETUDIANTS_BIS_PAGE_SIZE;
            const maxPages = typeof params.max_pages === "number" ? params.max_pages : 0;
            const {max_pages: _mp, limit: _limit, ...baseParams} = params;

            const aggregated = await fetchAllEtudiantsBisPages(
                (pageParams) => fetchEtudiantsBisPageSilent(pageParams),
                baseParams,
                {perPage, maxPages, useloading: false}
            );

            state.etudiants = aggregated.results;
            state.pagination.total = aggregated.count;
            state.pagination.nb_licence = aggregated.nb_licence ?? 0;
            state.pagination.nb_master = aggregated.nb_master ?? 0;
            state.pagination.current_page = 1;
            state.pagination.last_page =
                state.pagination.per_page === -1
                    ? 1
                    : Math.max(1, Math.ceil(aggregated.count / Math.max(1, state.pagination.per_page)));

            nextUrl.value = null;
            prevUrl.value = null;
            lastFetchTruncated.value = aggregated.truncated;

            return {
                ...aggregated,
                results: aggregated.results,
                count: aggregated.count,
                next: null,
                previous: null,
                nb_licence: aggregated.nb_licence,
                nb_master: aggregated.nb_master,
            };
        } catch (err: any) {
            state.errors.general = [
                (err?.message as string) || "Erreur lors de la récupération des étudiants",
            ];
            state.etudiants = [];
            state.pagination.total = 0;
            state.pagination.nb_licence = 0;
            state.pagination.nb_master = 0;
            lastFetchTruncated.value = false;
            throw err;
        } finally {
            if (useloading) state.loading = false;
        }
    }

    const refreshStudentList = () => fetchAllEtudiants();

    async function deleteInscription(slug: string) {
        loadingSubmit.value = true;
        state.errors = {};
        try {
            await apiDjangoAuth(`/inscription/${slug}/`, {method: "DELETE"});
            // Agrégation complète — pas un fetch mono-page qui tronquerait la liste
            await fetchAllEtudiants();
            return {success: true, message: "Inscription supprimée avec succès"};
        } catch (err: any) {
            //console.error("Erreur lors de deleteInscription:", err);
            state.errors.general = [
                (err?.message as string) || "Erreur lors de la suppression",
            ];
            return {success: false, message: state.errors.general[0]};
        } finally {
            loadingSubmit.value = false;
        }
    }

    // --- Exports
    return {
        // Actions
        fetchEtudiants,
        fetchAllEtudiants,
        fetchEtudiantsBisPageSilent,
        filterInformation,
        refreshStudentList,
        deleteInscription,

        // Navigation
        loadNext,
        loadPrevious,
        goToPage,
        loadingSubmit,
        // onUpdateSortBy,

        // État
        students: computed(() => state.etudiants),
        loading: computed(() => state.loading),
        errors: computed(() => state.errors),
        pagination: state.pagination,

        CLIENT_SIDE_LIMIT,
        lastFetchTruncated,
        nextUrl,
        prevUrl,

        // Compat héritée (pour éviter de casser un destructuring existant)
        nextAll: computed(() => null),
        prevAll: computed(() => null),
        nextNone: computed(() => null),
        prevNone: computed(() => null),
    };
}
